import React, { useState, useContext, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import find from 'lodash/find'
import isEqual from 'lodash/isEqual'
import PropTypes from 'prop-types'
import wishlistIcon from '../../img/wishlist-heart.svg'

import StoreContext from '~/context/StoreContext'
import WishlistContext from '~/context/WishlistContext'

const ProductForm = ({ product }) => {
  const {
    options,
    variants,
    variants: [initialVariant],
    priceRange: { minVariantPrice },
  } = product
  const [variant, setVariant] = useState({ ...initialVariant })
  const [quantity, setQuantity] = useState(1)
  const {
    addVariantToCart,
    store: { client, adding },
  } = useContext(StoreContext)

  const {
    addVariantToWishlist,
    // wishlistStore: { client, adding },
  } = useContext(WishlistContext)

  const productVariant =
    client.product.helpers.variantForOptions(product, variant) || variant
  const [available, setAvailable] = useState(productVariant.availableForSale)

  const checkAvailability = useCallback(
    productId => {
      client.product.fetch(productId).then(fetchedProduct => {
        // this checks the currently selected variant for availability
        const result = fetchedProduct.variants.filter(
          variant => variant.id === productVariant.shopifyId
        )
        if (result.length > 0) {
          setAvailable(result[0].available)
        }
      })
    },
    [client.product, productVariant.shopifyId, variants]
  )

  useEffect(() => {
    checkAvailability(product.shopifyId)
  }, [productVariant, checkAvailability, product.shopifyId])

  const handleQuantityChange = ({ target }) => {
    setQuantity(target.value)
  }

  const handleOptionChange = (optionIndex, { target }) => {
    const { value } = target
    const currentOptions = [...variant.selectedOptions]

    currentOptions[optionIndex] = {
      ...currentOptions[optionIndex],
      value,
    }

    const selectedVariant = find(variants, ({ selectedOptions }) =>
      isEqual(currentOptions, selectedOptions)
    )

    setVariant({ ...selectedVariant })
  }

  const handleAddToCart = () => {
    addVariantToCart(productVariant.shopifyId, quantity)
  }

  const handleAddToWishlist = () => {
    // console.log("added to wishlist")
    addVariantToWishlist(productVariant.shopifyId, quantity)
  }

  /*
  Using this in conjunction with a select input for variants
  can cause a bug where the buy button is disabled, this
  happens when only one variant is available and it's not the
  first one in the dropdown list. I didn't feel like putting
  in time to fix this since its an edge case and most people
  wouldn't want to use dropdown styled selector anyways -
  at least if the have a sense for good design lol.
  */
  const checkDisabled = (name, value) => {
    const match = find(variants, {
      selectedOptions: [
        {
          name: name,
          value: value,
        },
      ],
    })
    if (match === undefined) return true
    if (match.availableForSale === true) return false
    return true
  }

  const price = Intl.NumberFormat(undefined, {
    currency: minVariantPrice.currencyCode,
    minimumFractionDigits: 2,
    style: 'currency',
  }).format(variant.price)

  return (
    <>
      <WishlistWrapper>
        <img src={wishlistIcon} alt='Wishlist-Icon' onClick={handleAddToWishlist} style={{ width: '30px' }} />
      </WishlistWrapper>

      <h3 className='subtitle is-4' style={{ marginTop: '1rem', marginBottom: '.5rem', color: 'black' }}>{price}</h3>
      <p>Gemäß § 19 UStG wird keine Umsatzsteuer berechnet, zzgl. Versand</p>
      <div style={{ display: 'inline-block', marginTop: '1rem' }}>
        <label className='label' htmlFor='quantity'>Menge </label>
        <QntInput
          className='input'
          type="number"
          id="quantity"
          name="quantity"
          min="1"
          step="1"
          onChange={handleQuantityChange}
          value={quantity}
        />
      </div>
      {options.map(({ id, name, values }, index) => (
        <React.Fragment key={id}>
          <div style={{ display: 'inline-block', marginRight: '0.5rem' }}>
          <label className='label' htmlFor={name}>{name}</label>
          <Select
            className='select'
            style={{ borderRadius: '0px' }}
            name={name}
            key={id}
            onChange={event => handleOptionChange(index, event)}
          >
            {values.map(value => (
              <option
                value={value}
                key={`${name}-${value}`}
                disabled={checkDisabled(name, value)}
              >
                {value}
              </option>
            ))}
          </Select>
          </div>
        </React.Fragment>
      ))}

      <div style={{ display: 'block', marginTop: '1rem' }}>
        <button
          className='button is-medium is-primary is-uppercase'
          type="submit"
          disabled={!available || adding}
          onClick={handleAddToCart}
        >
          In den Warenkorb
        </button>
        {!available && <p>Produkt ist derzeit leider nicht verfügbar!</p>}
      </div>
    </>
  )
}

ProductForm.propTypes = {
  product: PropTypes.shape({
    descriptionHtml: PropTypes.string,
    handle: PropTypes.string,
    id: PropTypes.string,
    shopifyId: PropTypes.string,
    images: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        originalSrc: PropTypes.string,
      })
    ),
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
        values: PropTypes.arrayOf(PropTypes.string),
      })
    ),
    productType: PropTypes.string,
    title: PropTypes.string,
    variants: PropTypes.arrayOf(
      PropTypes.shape({
        availableForSale: PropTypes.bool,
        id: PropTypes.string,
        price: PropTypes.string,
        title: PropTypes.string,
        shopifyId: PropTypes.string,
        selectedOptions: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            value: PropTypes.string,
          })
        ),
      })
    ),
  }),
  addVariantToCart: PropTypes.func,
}

export default ProductForm

const QntInput = styled.input`
  width: 50px !important;
  border-radius: 0px !important;
  height: 38px !important;
  margin-bottom: 10px;
  margin-right: 20px;
  border-color: rgb(166, 166, 166) !important;
  // outline-color: orange !important;
  :focus {
    box-shadow: 0 0 0 2px orange !important;
    border-color: transparent !important;
    outline: 0;
    transition: box-shadow 0.15s ease-in-out;
  }
`

const Select = styled.select`
  appearance: none;
  /* stylelint-disable */
  background-image: url("data:image/svg+xml;charset=utf8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 4 5'%3E%3Cpath fill='black' d='M2 0L0 2h4zm0 5L0 3h4z'/%3E%3C/svg%3E");
  /* stylelint-enable */
  background-position: right 0.5rem center;
  background-repeat: no-repeat;
  background-size: 8px 10px;
  padding-right: 20px !important;
  padding-left: 10px !important;
  height: 38px !important;
  background-color: #fff;
  font-family: Montserrat !important;
  font-size: 16px;
  :focus {
    box-shadow: 0 0 0 2px orange !important;
    border-color: transparent !important;
    outline: 0;
    transition: box-shadow 0.15s ease-in-out;
  }
`

const WishlistWrapper = styled.div`
  margin-top: 2rem;
  &:hover{
    cursor: pointer;
  }
`
